import React, { JSX } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSmile,
    faTags,
    faFileAlt,
    faBolt,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';

interface AnalysisFeature {
    icon: JSX.Element;
    title: string;
    text: string;
    bg: string;
}

const analysisFeatures: AnalysisFeature[] = [
    {
        icon: <FontAwesomeIcon icon={faSmile} style={{ color: '#4CAF50' }} />,
        title: 'Sentiment Analysis',
        text: 'Detect and interpret customer sentiment in real-time.',
        bg: '#F0FFF4',
    },
    {
        icon: <FontAwesomeIcon icon={faTags} style={{ color: '#007BFF' }} />,
        title: 'Call Categorization',
        text: 'Automatically classify call topics and intents.',
        bg: '#F3F8FF',
    },
    {
        icon: <FontAwesomeIcon icon={faFileAlt} style={{ color: '#6C63FF' }} />,
        title: 'Call Summary',
        text: 'Generate concise, AI-driven summaries of conversations.',
        bg: '#F5F4FF',
    },
    {
        icon: <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#FF9800' }} />,
        title: 'Call Severity Detection',
        text: 'Identify urgent or escalated calls with smart severity scoring.',
        bg: '#FFF7F0',
    },
    {
        icon: <FontAwesomeIcon icon={faFileAlt} style={{ color: '#9C27B0' }} />,
        title: 'Keyword Extraction',
        text: 'Extract important terms and topics from conversations.',
        bg: '#FAF0FF',
    },
    {
        icon: <FontAwesomeIcon icon={faBolt} style={{ color: '#FF5722' }} />,
        title: 'Priority Detection',
        text: 'Automatically identify and label high-priority calls.',
        bg: '#FFF3EE',
    }
];

const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.2,
            duration: 0.6,
            ease: 'easeOut',
        },
    }),
};

const AnalysisFeatures: React.FC = () => {
    return (
        <section
            className="py-5"
            style={{
                backgroundImage: 'url(Analysis-background.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
            }}
        >
            <Container>
                <motion.h2
                    className="text-center mb-5"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    Intelligent Analysis Features
                </motion.h2>

                <Row className="g-4 justify-content-center">
                    {analysisFeatures.map((feature, index) => (
                        <Col key={index} xs={12} sm={6} md={4} className="d-flex">
                            <motion.div
                                custom={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={cardVariants}
                            >
                                <Card className="h-100 w-100 border-0 shadow-sm p-4" style={{ backgroundColor: feature.bg }}>
                                    <Card.Body className="d-flex flex-column">
                                        <div className="d-flex align-items-center mb-2">
                                            <div className="me-2">{feature.icon}</div>
                                            <Card.Title className="mb-0">{feature.title}</Card.Title>
                                        </div>
                                        <Card.Text>{feature.text}</Card.Text>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
};

export default AnalysisFeatures;
